import * as React from 'react';
import { useRef } from "react";
import Menu from '@mui/material/Menu';
import {DownArrowLightIcon, UpArrowLightIcon, IconMoreHorizon} from "../buttons/IconButtons"
import { useTranslation } from "react-i18next";
import {CustomDropdownMenuStyles} from "../Styles";
import {IconButton} from "@mui/material";

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 *
 * DropDownMenuProfile component renders a dropdown menu in the profile section.
 *
 * @param {string} openMenu - Identifier for the currently open menu.
 * @param {Function} handleMenuToggle - Function to toggle the menu open/close state.
 * @param {string} menuId - Unique identifier for this dropdown menu.
 * @param {string} name - Label for the menu button; if not provided, a default icon is shown.
 * @param {React.ReactNode} children - Menu items to be rendered inside the dropdown.
 * @param isAccordion - Boolean function to identify if it's inside an accordion.
 * @param {string} className - Additional classNames for styling the inner label/button.
 * @param {string} extraClassName - Additional class for button wrapper (default: 'btn-icon-svg-general').
 * @param {boolean} isLeftIconArrow - If true, arrow icon appears on the left side of the text.
 * @param {JSX.Element} IconAdditional - Icon element to render (optional)
 * @param {JSX.Element} icon - Icon for the compact mode (when `name` is not passed, optional).
 * @param classNameMenu className - Additional classNames for styling the inner menu
 * @param iconOptions - Option for icon
 * @param classNameIconContainer - dinamic options of classname
 * @returns {JSX.Element}
 */
const DropDownMenuProfile = ({
    openMenu,
    handleMenuToggle,
    menuId,
    name,
    children,
    isAccordion = false,
    className = '',
    extraClassName="btn-icon-svg-general",
    isLeftIconArrow=true,
    IconAdditional=null,
    icon = null,
    classNameMenu='',
    iconOptions = <IconMoreHorizon />,
    classNameIconContainer= ''
}) => {
    const menuButtonRef = useRef(null);
    const isOpen = openMenu === menuId;
    const { t } = useTranslation();

    const ComponentArrowIcon = ({isOpen}) => {
        return (
            <div className={`icon ${isOpen ? 'rotate' : ''}`}>
                {isOpen ? <UpArrowLightIcon /> : <DownArrowLightIcon/> }
            </div>
        )
    }
    const StyledIconOptions = React.isValidElement(iconOptions)
        ? React.cloneElement(iconOptions, { sx: CustomDropdownMenuStyles.iconButton })
        : iconOptions;
    const validChildren = React.Children.toArray(children).filter(Boolean);
    return (
        <>
            <div id="nboh_appbar_dropdown" className={`dropdown sidebar-menu ${ isAccordion ? "d-inline-flex" : ""}`}>
                <button
                    id="nboh_btn_appbar_dropdown"
                    ref={menuButtonRef}
                    className={`custom-dropdown d-flex justify-content-center align-items-center ${isOpen ? 'menu-open' : ''}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleMenuToggle(menuId);
                    }}
                >
                    {name ? (
                        <button className={`btn-icon-svg ${extraClassName} flex-row ${className}`}>
                            {isLeftIconArrow &&
                                <ComponentArrowIcon
                                    isOpen={isOpen}
                                />
                            }

                            {(IconAdditional != null && !isLeftIconArrow) &&
                                <IconAdditional/>
                            }

                            <span className='ms-1 me-1 d-sm-inline ibl-font-w-600 fs-14px'>
                                {t(name)}
                            </span>

                            {!isLeftIconArrow &&
                                <ComponentArrowIcon
                                    isOpen={isOpen}
                                />
                            }
                        </button>
                    ) : (
                        <IconButton className={`${classNameIconContainer}`} disableRipple>
                            { icon ? icon : StyledIconOptions}
                        </IconButton>
                    )}
                </button>
            </div>

            <Menu
                id={menuId}
                className={`${classNameMenu} "custom-paper"`}
                anchorEl={menuButtonRef?.current || undefined}
                open={isOpen}
                onClose={() => handleMenuToggle(menuId)}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
                {validChildren.map((child, index) => (
                    <React.Fragment key={index}>
                        {child}
                        {index !== validChildren.length - 1 && (
                            <div className='menu-divider' />
                        )}
                    </React.Fragment>
                ))}
            </Menu>
        </>
    );
};

export default DropDownMenuProfile;
